import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { NgxCurrencyDirective } from 'ngx-currency';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { map, catchError, of } from 'rxjs';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { SelectPesquisavelService } from '@shared/components/select-pesquisavel/select-pesquisavel.service';
import {
    PesquisaCodigoTributacaoModalComponent,
    PesquisaCodigoTributacaoModalData,
    PesquisaCodigoTributacaoModalResult,
} from '@shared/components/pesquisa-codigo-tributacao-modal/pesquisa-codigo-tributacao-modal.component';
import { MaskService } from '@shared/services/mask.service';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { CsosnStruct } from '@shared/services/structs/motor-fiscal/csosn.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { getCsosnTexto, CsosnTexto } from '@shared/constants/motor-fiscal/csosn-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';

/** Campo do indicador do CSOSN -> control da regra da natureza (aba e wizard de exceção usam os mesmos nomes). */
export const MAPA_CAMPOS_CSOSN_NATUREZA: MapaCamposIndicador = {
    pCredSN: 'pCredIcmsSn',
    modBC: 'modalidadeBc',
    pRedBC: 'pRedBc',
    pICMS: 'pIcms',
    modBCST: 'modalidadeBcSt',
    pMVAST: 'pMvaStIcmsSn',
    pRedBCST: 'pRedBcSt',
    pICMSST: 'pIcmsSt',
    pFCPST: 'pFcpSt',
    pST: 'pSt',
    pFCPSTRet: 'pFcpStRet',
    pRedBCEfet: 'pRedBcEfet',
    pICMSEfet: 'pIcmsEfet',
    obterStCompra: 'obterIcmsStCompra',
};

@Component({
    selector: 'app-icms-sn-tributo-regra-section',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        MatButtonModule,
        MatBadgeModule,
        MatIconModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        NgxMaskDirective,
    ],
    providers: [provideNgxMask()],
    templateUrl: './tributo-regra-section.component.html',
    styleUrl: './tributo-regra-section.component.css',
})
export class SnTributoRegraSectionComponent implements OnChanges {
    @Input({ required: true }) form!: FormGroup;
    @Input() csosns: CsosnStruct[] = [];
    @Input() cfopLookupUrl = '';
    @Input() variaveis: VariavelStruct[] = [];
    @Input() personalizadasCount = 0;
    @Input() modoExcecao = false;
    @Input() personalizarDisabled = false;
    @Input() cfopControlName = 'cfopPadrao';
    /** Tipo de operação da natureza (enum inteiro). Define os dígitos do CFOP no tooltip. */
    @Input() tipoOperacao: TipoOperacaoEnum = TipoOperacaoEnum.Saida;
    @Output() personalizar = new EventEmitter<void>();

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly masks = MaskService.getMasks();
    /** Permite o coringa "?" na 1a posicao do CFOP (a regra por posicao e garantida no onCfopKeydown). */
    public readonly cfopPatterns = { '0': { pattern: /[0-9?]/ } };

    public readonly cfopEntradaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do fornecedor.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '1 - Operação dentro da mesma UF\n' +
        '2 - Operação interestadual\n' +
        '3 - Operação de importação';

    public readonly cfopSaidaTooltipInfo =
        'Use "?" no início do CFOP\n' +
        'para substituição automática\n' +
        'conforme a UF do cliente.\n\n' +
        'Exemplo: Para o CFOP ?102,\n' +
        'o "?" será substituído por:\n\n' +
        '5 - Operação dentro da mesma UF\n' +
        '6 - Operação interestadual\n' +
        '7 - Operação de exportação';

    /** Tooltip do "?" conforme o tipo de operação (entrada usa 1/2/3, saída usa 5/6/7). */
    public get cfopTooltipInfo(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? this.cfopEntradaTooltipInfo : this.cfopSaidaTooltipInfo;
    }

    /** Dígitos válidos para a 1ª posição do CFOP conforme o tipo de operação. */
    public get cfopPrimeirosDigitos(): string[] {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? ['1', '2', '3'] : ['5', '6', '7'];
    }

    public get cfopPlaceholder(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada ? 'Ex: 1102, ?102, ?101' : 'Ex: 5102, ?102, ?101';
    }

    @Input() modalidadesBc: ModalidadeBcOption[] = [];
    @Input() modalidadesBcSt: ModalidadeBcOption[] = [];

    /** Rótulo do toggle DIFAL, independente do CSOSN — muda só pela direção da natureza. */
    public get difalLabel(): string {
        return this.tipoOperacao === TipoOperacaoEnum.Entrada
            ? 'Considerar DIFAL/antecipação na entrada'
            : 'Habilitar DIFAL da operação interestadual';
    }

    constructor(
        private maskService: MaskService,
        private dialog: MatDialog,
        private selectPesquisavelService: SelectPesquisavelService,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['form'] && this.form) {
            // Zera os campos que o novo CSOSN não exibe (evita valor obsoleto no payload).
            this.form.get('csosn')?.valueChanges.subscribe(() =>
                limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_CSOSN_NATUREZA));
        }
    }

    public get indicadores(): IndicadoresCst {
        const csosn = this.form?.get('csosn')?.value ?? null;
        return lerIndicadoresCst(this.csosns.find(c => c.codigo === csosn)?.indicadores);
    }

    public get csosnTexto(): CsosnTexto | null {
        return getCsosnTexto(this.form?.get('csosn')?.value ?? null);
    }

    /** Só os CSOSN da operação (entrada ou saída) da natureza, pelo indicador. */
    public get csosnsDaOperacao(): CsosnStruct[] {
        return this.csosns.filter(c => isCodigoDaOperacao(c.indicadores, this.tipoOperacao));
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    public get cfopControl(): FormControl {
        return this.form.get(this.cfopControlName) as FormControl;
    }

    /** True quando o botão "Personalizar fórmulas" não pode ser acessado. */
    public get personalizarBloqueado(): boolean {
        return this.personalizarDisabled || !this.form?.get('csosn')?.value;
    }

    /** Mensagem do tooltip exibida apenas quando o botão está desabilitado. */
    public get personalizarTooltip(): string {
        if (this.personalizarDisabled) {
            return this.modoExcecao
                ? 'A personalização de fórmulas não está disponível para exceções.'
                : 'A personalização de fórmulas está indisponível no momento.';
        }
        if (!this.form?.get('csosn')?.value) {
            return 'Selecione um CSOSN para habilitar a personalização de fórmulas.';
        }
        return '';
    }

    public onPersonalizar(): void {
        this.personalizar.emit();
    }

    /**
     * Valida entrada no campo CFOP (máx 4 dígitos). Na primeira posição aceita "?" ou
     * apenas os dígitos válidos do tipo de operação (entrada: 1/2/3; saída: 5/6/7);
     * nas demais posições aceita qualquer dígito.
     */
    public onCfopKeydown(event: KeyboardEvent): void {
        const input = event.target as HTMLInputElement;
        const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        const isShortcut = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
        if (allowed.includes(event.key) || isShortcut) return;

        const selStart = input.selectionStart ?? 0;

        // Primeira posição: aceita "?" ou apenas os dígitos válidos do tipo de operação.
        if (selStart === 0) {
            if (event.key === '?' || this.cfopPrimeirosDigitos.includes(event.key)) return;
            event.preventDefault();
            return;
        }

        // Demais posições: somente dígitos.
        if (!/^\d$/.test(event.key)) { event.preventDefault(); }
    }

    public pesquisarCfop(): void {
        const cfopUrl = `${this.cfopLookupUrl}?idTipoOperacao=${this.tipoOperacao}`;
        const dialogRef = this.dialog.open(PesquisaCodigoTributacaoModalComponent, {
            width: '700px',
            maxHeight: '85vh',
            panelClass: 'pesquisa-codigo-tributacao-dialog',
            data: {
                titulo: 'Pesquisar CFOP',
                buscarFn: (pesquisa: string) =>
                    this.selectPesquisavelService.getSelectValues<any>(cfopUrl, pesquisa).pipe(
                        map(list => ({
                            isError: false,
                            itens: list.map((i: any) => ({ codigo: String(i.codigo ?? ''), descricao: i.descricao })),
                        })),
                        catchError(() => of({ isError: true, itens: [] })),
                    ),
                permitirManual: false,
                registrosIniciais: 20,
                valorInicial: this.cfopControl?.value ?? undefined,
            } as PesquisaCodigoTributacaoModalData,
        });

        dialogRef.afterClosed().subscribe((result: PesquisaCodigoTributacaoModalResult | null) => {
            if (!result?.selecionado) return;
            this.cfopControl?.setValue(result.selecionado.codigo.replace(/\D/g, ''));
        });
    }
}
